(() => {
  if (!customElements.get('variant-selects')) {
    customElements.define(
      'variant-selects',
      class VariantSelects extends HTMLElement {
        constructor() {
          super();
          this.addEventListener('change', this.onVariantChange.bind(this));
        }

        connectedCallback() {
          this.updateOptionAvailability();
        }

        getVariantData() {
          if (!this.variantData) {
            const script = this.querySelector('script[type="application/json"]');
            this.variantData = script ? JSON.parse(script.textContent) : [];
          }
          return this.variantData;
        }

        getInputGroups() {
          return Array.from(this.querySelectorAll('.product-form__input'));
        }

        getSelectedOptionValues() {
          return this.getInputGroups().map((group) => {
            const select = group.querySelector('select');
            if (select) return select.value;
            const checked = group.querySelector('input[type="radio"]:checked');
            return checked ? checked.value : undefined;
          });
        }

        findVariant(optionValues) {
          return this.getVariantData().find(
            (variant) =>
              Array.isArray(variant.options) &&
              variant.options.length === optionValues.length &&
              variant.options.every((value, index) => value === optionValues[index])
          );
        }

        onVariantChange(event) {
          if (!event.target.matches('input[type="radio"], select')) return;

          this.updateOptionAvailability();

          const optionValues = this.getSelectedOptionValues();
          const variant = this.findVariant(optionValues);
          this.currentVariant = variant;

          this.updateVariantInput(variant);
          try {
            this.updateURL(variant);
          } catch (error) {
            console.error(error);
          }

          this.dispatchEvent(
            new CustomEvent('variant-change', {
              bubbles: true,
              detail: { variant, sectionId: this.dataset.section },
            })
          );

          if (window.publish && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.variantChange, {
              data: { sectionId: this.dataset.section, variant },
            });
          }
        }

        updateOptionAvailability() {
          const groups = this.getInputGroups();
          const selectedValues = this.getSelectedOptionValues();
          const variants = this.getVariantData();
          const skipUnavailable = this.dataset.skipUnavailable === 'true';

          groups.forEach((group, position) => {
            const options = Array.from(group.querySelectorAll('option'));
            const isDropdown = options.length > 0;
            const candidates = isDropdown ? options : Array.from(group.querySelectorAll('input[type="radio"]'));

            candidates.forEach((candidate) => {
              const value = candidate.value;
              let exists = false;
              let available = false;

              for (const variant of variants) {
                let prefixMatches = true;
                for (let p = 0; p < position; p++) {
                  if (variant.options[p] !== selectedValues[p]) {
                    prefixMatches = false;
                    break;
                  }
                }
                if (!prefixMatches) continue;
                if (variant.options[position] === value) {
                  exists = true;
                  if (variant.available) {
                    available = true;
                    break;
                  }
                }
              }

              const target = candidate.closest('.color-swatch') || candidate;
              target.classList.remove('non-existent', 'unavailable');
              candidate.classList.remove('disabled');

              if (!exists) {
                target.classList.add('non-existent');
              } else if (!available) {
                target.classList.add('unavailable');
                candidate.classList.add('disabled');
              }

              if (isDropdown) {
                const hidden = !exists || (exists && !available && skipUnavailable);
                candidate.disabled = hidden;
                candidate.hidden = hidden;
              }
            });
          });
        }

        updateVariantInput(variant) {
          const productForm = document.getElementById(`product-form-${this.dataset.section}`);
          if (!productForm) return;
          const input = productForm.querySelector('input[name="id"]');
          if (input) input.value = variant ? variant.id : '';
        }

        updateURL(variant) {
          if (this.dataset.updateUrl === 'false' || !variant || !this.dataset.url) return;
          window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
        }
      }
    );
  }

  if (!customElements.get('quantity-input')) {
    customElements.define(
      'quantity-input',
      class QuantityInput extends HTMLElement {
        constructor() {
          super();
          this.input = this.querySelector('input');
          this.changeEvent = new Event('change', { bubbles: true });
          this.querySelectorAll('button').forEach((button) =>
            button.addEventListener('click', this.onButtonClick.bind(this))
          );
        }

        onButtonClick(event) {
          event.preventDefault();
          if (!this.input) return;
          const previousValue = this.input.value;
          if (event.currentTarget.name === 'plus') this.input.stepUp();
          else this.input.stepDown();
          if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
        }
      }
    );
  }

  if (!customElements.get('custom-product-field')) {
    customElements.define(
      'custom-product-field',
      class CustomProductField extends HTMLElement {
        constructor() {
          super();
          this.required = this.dataset.required === 'true';
          this.errorMsg = this.dataset.atcErrorMsg || '';
          this.input = this.querySelector('input[type="text"], input[type="number"], textarea, select');
          this.radios = Array.from(this.querySelectorAll('input[type="radio"]'));
          this.submitButton = document.getElementById(`ProductSubmitButton-${this.dataset.section}`);

          this.isValid = true;

          if (this.required && this.submitButton) {
            this.submitButton.dataset.requiredFields = String(
              Number(this.submitButton.dataset.requiredFields || 0) + 1
            );
          }

          this.updateValidState(this.getValue(), true);

          if (this.input) {
            this.input.addEventListener('input', () => this.updateValidState(this.getValue()));
            this.input.addEventListener('change', () => this.updateValidState(this.getValue()));
          }
          this.radios.forEach((radio) =>
            radio.addEventListener('change', () => this.updateValidState(this.getValue()))
          );
        }

        getValue() {
          if (this.radios.length) {
            const checked = this.radios.find((radio) => radio.checked);
            return checked ? checked.value : '';
          }
          return this.input ? this.input.value : '';
        }

        updateValidState(value, isInitial = false) {
          const wasValid = this.isValid;
          this.isValid = !this.required || value.trim().length > 0;

          if (this.input && !this.radios.length) {
            this.input.classList.toggle('input--error', !this.isValid);
          }

          if (!this.required || !this.submitButton) return;

          if (isInitial) {
            if (this.isValid) {
              this.submitButton.dataset.validFields = String(Number(this.submitButton.dataset.validFields || 0) + 1);
            }
            return;
          }

          if (this.isValid && !wasValid) {
            this.submitButton.dataset.validFields = String(Number(this.submitButton.dataset.validFields || 0) + 1);
          } else if (!this.isValid && wasValid) {
            this.submitButton.dataset.validFields = String(
              Math.max(0, Number(this.submitButton.dataset.validFields || 0) - 1)
            );
          }

          const errorTarget = this.submitButton.querySelector('.main-atc__error');
          if (!errorTarget) return;
          if (!this.isValid) {
            errorTarget.textContent = this.errorMsg;
            errorTarget.style.display = '';
          } else if (Number(this.submitButton.dataset.validFields) >= Number(this.submitButton.dataset.requiredFields)) {
            errorTarget.style.display = 'none';
          }
        }
      }
    );
  }

  if (!customElements.get('product-form')) {
    customElements.define(
      'product-form',
      class ProductForm extends HTMLElement {
        constructor() {
          super();
          this.form = this.querySelector('form');
          if (!this.form) return;
          this.form.addEventListener('submit', this.onSubmit.bind(this));
          this.submitButton = this.querySelector('[type="submit"]');
          this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
          this.errorMessage = this.querySelector('.product-form__error-message');
        }

        handleSubmit(event) {
          this.onSubmit(event);
        }

        onSubmit(event) {
          event.preventDefault();
          if (!this.form) return;
          if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') return;

          const requiredFields = Number(this.submitButton?.dataset.requiredFields || 0);
          const validFields = Number(this.submitButton?.dataset.validFields || 0);
          if (requiredFields > validFields) {
            const errorTarget = this.submitButton.querySelector('.main-atc__error');
            if (errorTarget) errorTarget.style.display = '';
            return;
          }

          this.handleErrorMessage(false);
          this.setLoading(true);

          const formData = new FormData(this.form);
          const cartUI = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          const offer = this.closest('bundle-deals, main-bundle-offer') || document.querySelector('main-bundle-offer');
          const quantityBreaks = document.querySelector(`quantity-breaks[data-section="${this.dataset.section}"]`);
          const items = offer?.getSelectedItems?.() || quantityBreaks?.getSelectedItems?.();

          if (cartUI && typeof cartUI.getSectionsToRender === 'function') {
            const sections = cartUI.getSectionsToRender().map((section) => section.section || section.id).join(',');
            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);
            if (items?.length) {
              items.sections = sections;
              items.sections_url = window.location.pathname;
            }
          }
          if (cartUI && typeof cartUI.setActiveElement === 'function') {
            cartUI.setActiveElement(document.activeElement);
          }

          const requestConfig = window.fetchConfig ? window.fetchConfig('javascript') : { method: 'POST' };
          if (!items?.length) {
            requestConfig.headers = { ...(requestConfig.headers || {}) };
            delete requestConfig.headers['Content-Type'];
            delete requestConfig.headers['content-type'];
          }

          fetch(`${window.routes.cart_add_url}.js`, {
            ...requestConfig,
            ...(items?.length ? { headers: { Accept: 'application/javascript', 'Content-Type': 'application/json' } } : {}),
            body: items?.length ? JSON.stringify({ items, sections: items.sections, sections_url: items.sections_url }) : formData,
          })
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
                this.handleErrorMessage(response.description || response.message || true);
                return;
              }

              this.handleErrorMessage(false);

              if (window.publish && window.PUB_SUB_EVENTS) {
                window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                });
              }

              if (cartUI && typeof cartUI.renderContents === 'function') {
                cartUI.renderContents(response);
              } else if (window.routes?.cart_url) {
                window.location.href = window.routes.cart_url;
              }
            })
            .catch((error) => {
              console.error(error);
            })
            .finally(() => {
              this.setLoading(false);
            });
        }

        setLoading(loading) {
          if (!this.submitButton) return;
          const spinner = this.submitButton.querySelector('.loading-overlay__spinner');
          spinner?.classList.toggle('hidden', !loading);
          this.submitButton.setAttribute('aria-disabled', loading);
          this.submitButton.disabled = loading || this.submitButton.dataset.unavailable === 'true';
        }

        handleErrorMessage(message) {
          if (!this.errorWrapper) return;
          const hasMessage = Boolean(message);
          this.errorWrapper.toggleAttribute('hidden', !hasMessage);
          if (this.errorMessage) this.errorMessage.textContent = typeof message === 'string' ? message : '';
        }
      }
    );
  }

  if (!customElements.get('product-info')) {
    customElements.define(
      'product-info',
      class ProductInfo extends HTMLElement {
        connectedCallback() {
          this.variantSelects = this.querySelector('variant-selects');
          if (this.variantSelects) {
            this.variantSelects.addEventListener('variant-change', this.handleVariantChange.bind(this));
          }
        }

        initShareLinks() {
          // assets/share.js already implements the local share-button custom element
          // independently; no additional wiring is required here.
        }

        handleVariantChange(event) {
          const { variant } = event.detail;
          this.updateAddToCartButton(variant);
          this.updateMedia(variant);
          this.updateSectionMarkup(variant);
        }

        updateAddToCartButton(variant) {
          const button = document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
          if (!button) return;
          const unavailable = !variant || !variant.available;
          button.dataset.unavailable = unavailable ? 'true' : 'false';
          button.disabled = unavailable;

          const priceWrapper = document.getElementById(`main-atc-price-${this.dataset.section}`);
          priceWrapper?.classList.toggle('hidden', unavailable);
        }

        updateMedia(variant) {
          if (!variant || !variant.featured_media) return;

          const mediaId = `${this.dataset.section}-${variant.featured_media.id}`;
          const galleries = document.querySelectorAll(`media-gallery[data-section="${this.dataset.section}"]`);
          galleries.forEach((gallery) => {
            if (typeof gallery.setActiveMedia === 'function') {
              const filtering = Boolean(gallery.filteringOption);
              gallery.setActiveMedia(mediaId, true, filtering, variant);
            }
          });
        }

        updateSectionMarkup(variant) {
          if (!variant || !this.dataset.url || !this.dataset.section) return;

          fetch(`${this.dataset.url}?variant=${variant.id}&section_id=${this.dataset.section}`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');

              const priceId = `price-${this.dataset.section}`;
              const updatedPrice = html.getElementById(priceId);
              const currentPrice = document.getElementById(priceId);
              if (updatedPrice && currentPrice) currentPrice.innerHTML = updatedPrice.innerHTML;

              const quantityId = `Quantity-${this.dataset.section}`;
              const updatedQuantity = html.getElementById(quantityId);
              const currentQuantity = document.getElementById(quantityId);
              if (updatedQuantity && currentQuantity) {
                ['min', 'max', 'step', 'value', 'data-min', 'data-max', 'data-cart-quantity'].forEach((attr) => {
                  if (updatedQuantity.hasAttribute(attr)) {
                    currentQuantity.setAttribute(attr, updatedQuantity.getAttribute(attr));
                  } else {
                    currentQuantity.removeAttribute(attr);
                  }
                });
              }

              const updatedAtcPrice = html.querySelector(`.main-atc-price-span-${this.dataset.section}`);
              if (updatedAtcPrice) {
                document
                  .querySelectorAll(`.main-atc-price-span-${this.dataset.section}`)
                  .forEach((el) => (el.textContent = updatedAtcPrice.textContent));
              }
            })
            .catch(() => {});
        }
      }
    );
  }
})();
